import React, {useEffect, useRef, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from '../../../../modules/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {useLocation, useNavigate, useParams} from 'react-router-dom'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import Overlay from 'app/components/Overlay'
import DrawerFooter from '../drawer-components/DrawerFooter'
import InfoCard from '../drawer-components/InfoCard'
import {AddDrawerIntegrationProps} from '../utils/types'

const JIRA_CLIENT_ID = process.env.REACT_APP_JIRA_CLIENT_ID || ''
const JIRA_REDIRECT_URI = process.env.REACT_APP_JIRA_REDIRECT_URI || ''

const JiraDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [projectSelecting, setProjectSelecting] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const [selectedProject, setSelectedProject] = useState('')
  const [isProjectLocked, setIsProjectLocked] = useState(false)
  const [accessTokenJira, setAccessTokenJira] = useState<string | null>(
    localStorage.getItem('jira_access_token')
  )
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const navigate = useNavigate()
  const location = useLocation()
  const {code} = useParams()
  const queryParams = new URLSearchParams(location.search)
  const integrationIdFromUrl = queryParams.get('id') || id

  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'

  useEffect(() => {
    if (code) {
      localStorage.setItem('jira_access_token', code)
      navigate('/integrations', {replace: true})
    }
  }, [code, navigate])

  useEffect(() => {
    if (isOpen && id) {
      getIntegration();
    }
    if (isOpen && !selectedProject) {
      setFormErrors((prev) => ({ ...prev, selectedProject: 'Please select a project' }));
    } else if (isOpen && selectedProject) {
      setFormErrors((prev) => ({ ...prev, selectedProject: '' }));
    }
  }, [id, isOpen, selectedProject]);

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'Jira') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
              projects: data.projects || [],
            }
            setIntegration([integrationData])
            setAccessTokenJira(data.accessToken)
            if (data.projects?.length > 0 && data.projectId) {
              setSelectedProject(data.projectId)
              setIsProjectLocked(true)
            }
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  const handleConnect = async () => {
    if (!accessTokenJira) {
      setIsLoading(true)
      localStorage.setItem('pendingIntegration', 'Jira')
      const encodedRedirectUri = encodeURIComponent(JIRA_REDIRECT_URI)
      const authUrl = `https://auth.atlassian.com/authorize?audience=api.atlassian.com&client_id=${JIRA_CLIENT_ID}&scope=read%3Ajira-user%20read%3Ajira-work%20write%3Ajira-work&redirect_uri=${encodedRedirectUri}&state=${id}&response_type=code&prompt=consent`
      window.location.href = authUrl
      return
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    onClose()
  }

  const handleProjectSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectId = e.target.value
    setSelectedProject(projectId)

    if (!projectId) {
      setFormErrors((prev) => ({...prev, selectedProject: 'Please select a project'}))
      return
    }

    setFormErrors((prev) => ({...prev, selectedProject: ''}))

    try {
      setProjectSelecting(true)
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl || !tenantId) return

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      const selectedProjectData = integration[0].projects.find((p: any) => p.id === projectId)

      await updateDoc(integrationRef, {
        projectId,
        selectedProject: selectedProjectData,
      })

      setIsProjectLocked(true)
      showSuccessToast({message: 'Project selected successfully'})
    } catch (error) {
      console.error('Error handling project selection:', error)
      showErrorToast({message: 'Failed to process project selection'})
    } finally {
      setProjectSelecting(false)
    }
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!integrationIdFromUrl || !tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)

      const assetsRef = collection(tenantRef, 'assets')
      const jiraAssetsQuery = query(assetsRef, where('platform', '==', 'Jira'))
      const jiraAssetsSnapshot = await getDocs(jiraAssetsQuery)

      const deleteAssetPromises = jiraAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id))
      })

      await Promise.all(deleteAssetPromises)

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', integrationIdFromUrl)
      await deleteDoc(integrationRef)

      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'jira')
      await deleteDoc(accessReviewRef)

      const vendorsRef = collection(tenantRef, 'vendors')
      const jiraVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Jira'))
      const jiraVendorSnapshot = await getDocs(jiraVendorQuery)

      const deleteVendorPromises = jiraVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all(deleteVendorPromises)

      localStorage.removeItem('jira_access_token')

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Jira integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })

      showSuccessToast({message: 'Jira integration disconnected'})

      setIntegration([])
      setSelectedProject('')
      setIsProjectLocked(false)
      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error disconnecting Jira:', errorMessage)
      showErrorToast({message: 'Failed to disconnect Jira'})
    } finally {
      setIsLoading(false)
    }
  }

  const ProjectSelector = () => {
    if (!integration[0]?.projects?.length) return null

    return (
      <div className='mb-4'>
        <label className='fw-semibold mb-2 fs-6'>Select Project</label>
        <select
          className='form-select'
          value={selectedProject}
          onChange={handleProjectSelect}
          disabled={isProjectLocked}
        >
          <option value=''>Select Project</option>
          {integration[0].projects.map((project: any) => (
            <option key={project.id} value={project.id}>
              {project.name}
            </option>
          ))}
        </select>
        {formErrors.selectedProject && (
          <span className='text-danger'>{formErrors.selectedProject}</span>
        )}
        {isProjectLocked && (
          <small className='text-muted'>
            Project selection is locked. To change, disconnect the integration.
          </small>
        )}
      </div>
    )
  }

  return (
    <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
      <DrawerHeader title='Jira' onClose={onClose} />
      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
          <DrawerContent
            title='Jira'
            description='Project Management & Issue Tracking'
            logo='/media/integrations/Jira.svg'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will be granted permissions to read and write Jira issues and projects via OAuth 2.0, respecting user permissions.'
            accessTypeinfo='Requires an active Jira integration with sufficient permissions to access project data.'
          />
          <div className='mb-8 d-flex justify-content-between align-items-center'>
            <div className='fs-6 fw-bold text-bold'>
              {isConnected ? 'Jira Connected to SecureSlate' : 'Connect Jira to SecureSlate'}{' '}
              <Overlay content='Upon successful connection, all projects within your Jira workspace will be automatically imported and presented within the Asset Repository section of the platform for your review and management.' />
            </div>
            {projectSelecting && (
              <span className='badge px-3 rounded-pill ms-4' style={{border: '2px solid #E1E1E1'}}>
                <span className='fs-7'>Updating...</span>
              </span>
            )}
          </div>
          <ProjectSelector />
          {!isConnected &&
          <InfoCard
            type='info'
            title='Connect Jira to SecureSlate'
            description='Click on the "Connect" button below. You will be redirected to Jira to authenticate via OAuth and authorize SecureSlate. Once authorized, projects will be automatically fetched and configured.'
          />
          }
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default JiraDrawer
